import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const ACCENT = '#E52E3A';        // COROS Logo Red (VI 2023)
export const ACCENT_BRIGHT = '#FF4551'; // --coros-red-hi
export const TEAL = '#22C55E';          // system 'ok' green; teal is not a COROS colour

export const themes = {
  dark: {
    bg: '#0A0A18',
    surface: '#14142B',
    surfaceElevated: '#1B1B38',
    surfaceHover: '#1B1B38',
    glass: 'rgba(20, 20, 43, 0.65)',
    glassStrong: 'rgba(20, 20, 43, 0.85)',
    bgCard: '#14142B',
    bgHeader: 'rgba(11, 11, 33, 0.80)',
    bgInput: '#0E0E22',
    bgElevated: '#1B1B38',
    bgHover: '#1B1B38',
    border: 'rgba(255,255,255,0.06)',
    borderLight: 'rgba(255,255,255,0.04)',
    borderStrong: 'rgba(255,255,255,0.10)',
    borderInput: 'rgba(255,255,255,0.08)',
    borderAccent: 'rgba(229,46,58,0.2)',
    borderTeal: 'rgba(34,197,94,0.2)',
    text: '#F4F4F8',
    textSecondary: '#B4B6CC',
    textMuted: '#7A7C99',
    textDim: '#454966',
    textFaint: '#333850',
    textMeta: '#454966',
    divider: 'rgba(255,255,255,0.06)',
    accentGlow: 'rgba(229, 46, 58, 0.12)',
    accentGlowStrong: 'rgba(229, 46, 58, 0.25)',
    shadowCard: '0 2px 8px rgba(0,0,0,0.25)',
    shadowCardHover: '0 12px 32px rgba(0,0,0,0.45)',
    shadowElevated: '0 4px 24px rgba(0,0,0,0.35)',
    auroraOrange: 'rgba(229,46,58,0.07)',
    auroraTeal: 'rgba(34,197,94,0.04)',
    auroraBlue: 'rgba(59,130,246,0.03)',
    gridColor: 'rgba(255,255,255,0.018)',
  },
  light: {
    bg: '#F2F2F4',
    surface: '#FFFFFF',
    surfaceElevated: '#FFFFFF',
    surfaceHover: '#F5F5F8',
    glass: 'rgba(255, 255, 255, 0.6)',
    glassStrong: 'rgba(255, 255, 255, 0.85)',
    bgCard: '#FFFFFF',
    bgHeader: 'rgba(237, 238, 243, 0.85)',
    bgInput: '#F5F5F8',
    bgElevated: '#FFFFFF',
    bgHover: '#F8F8FC',
    border: 'rgba(0,0,0,0.06)',
    borderLight: 'rgba(0,0,0,0.04)',
    borderStrong: 'rgba(0,0,0,0.10)',
    borderInput: 'rgba(0,0,0,0.08)',
    borderAccent: 'rgba(229,46,58,0.15)',
    borderTeal: 'rgba(34,197,94,0.15)',
    text: '#0B0B21',
    textSecondary: '#45465C',
    textMuted: '#86889C',
    textDim: '#B0B4C4',
    textFaint: '#C8CCE0',
    textMeta: '#B0B4C4',
    divider: 'rgba(0,0,0,0.06)',
    accentGlow: 'rgba(229, 46, 58, 0.08)',
    accentGlowStrong: 'rgba(229, 46, 58, 0.15)',
    shadowCard: '0 1px 3px rgba(0,0,0,0.04), 0 6px 16px rgba(0,0,0,0.04)',
    shadowCardHover: '0 8px 28px rgba(0,0,0,0.1)',
    shadowElevated: '0 2px 16px rgba(0,0,0,0.06)',
    auroraOrange: 'rgba(229,46,58,0.04)',
    auroraTeal: 'rgba(34,197,94,0.025)',
    auroraBlue: 'rgba(59,130,246,0.02)',
    gridColor: 'rgba(0,0,0,0.018)',
  },
};

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem('scout-theme');
    return saved === 'light' ? 'light' : 'dark';
  });

  const toggle = () => {
    const next = mode === 'dark' ? 'light' : 'dark';
    setMode(next);
    localStorage.setItem('scout-theme', next);
  };

  const t = themes[mode];

  useEffect(() => {
    document.body.style.background = t.bg;
    document.body.style.color = t.text;
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode, t]);

  return (
    <ThemeContext.Provider value={{ mode, toggle, t }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
